import { notifications } from '@mantine/notifications';
import { useApi } from 'api/api-context';
import useImmediateAction from 'api/use-immediate-action';
import RequireGlobalPermissionPage from 'components/permissions/RequireGlobalPermissionPage';
import OrganizationForm, { OrganizationFormData } from 'components/forms/organization/OrganizationForm';
import { ORGANIZATION_REDIRECT_AFTER_SAVE } from 'env';
import panic from 'errors/panic';
import DashboardLayout from 'layouts/dashboard/DashboardLayout';
import { getDeviceType } from 'model/DeviceType';
import { usePermissionDataProvider } from 'model/PermissionDataProvider';
import { nanoid } from 'nanoid';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { ORGANIZATIONS_PAGE_PATH } from 'routes/paths';
import { SUCCESS_NOTIFICATION_COLOR } from 'utils/constants';

/**
 * Page used to edit the organization.
 *
 */
export default function EditOrganizationPage() {
  return (
    <RequireGlobalPermissionPage permission="manage-organizations">
      <EditOrganizationPageContent />
    </RequireGlobalPermissionPage>
  );
}

/**
 * Page content used to edit an organization.
 */
function EditOrganizationPageContent() {
  const navigate = useNavigate();
  const { organizationId } = useParams();
  const { getAction } = useApi();
  const { getPermissionTemplate, getDefaultPermissionTemplate } = usePermissionDataProvider();

  const { data, loading, error } = useImmediateAction(() => {
    const organizationGetAction = getAction('OrganizationGet');
    return organizationGetAction({ parameters: { organizationId: organizationId! } });
  });

  if (error) {
    panic(error);
    return <></>;
  }

  if (!organizationId) {
    return <Navigate to={ORGANIZATIONS_PAGE_PATH.original} />;
  }

  /**
   * Handles the form submission.
   */
  function onSubmit({
    organizationName,
    officialName,
    streetAddress,
    city,
    zip,
    country,
    ico,
    dic,
    icdph,
    companyQualifications,
    people,
    includedInStatistics,
    inKepHashArchive,
    priceOffersEnabled,
    hrEnabled,
    revisionsEnabled,
    formsEnabled,
    revisionTemplatesEnabled,
    logoFileId,
  }: OrganizationFormData) {
    const organizationUpdateAction = getAction('OrganizationUpdate');

    const mappedPeople = Object.entries(people)
      .map(([personId, value]) => ({
        userId: Number(personId),
        checked: value.checked,
        deleted: value.deleted,
        permissions: value.permissions,
        templateId:
          getPermissionTemplate(value.templateName)?.permissionTemplateId ??
          getDefaultPermissionTemplate().permissionTemplateId,
        isNew: value.isNew,
      }))
      .map(({ userId, permissions, templateId, deleted, isNew }) => ({
        deleted,
        userId,
        permissions: permissions ?? [],
        templateId,
        isNew,
      }));

    return organizationUpdateAction({
      payload: {
        organizationName,
        officialName,
        streetAddress,
        city,
        zip,
        country,
        ico,
        dic,
        icdph,
        companyQualifications: (companyQualifications || []).map(({ qualificationNumber, type }) => ({
          deviceTypeId: getDeviceType(type)!.id,
          qualificationNumber,
        })),
        people: mappedPeople,
        includedInStatistics,
        inKepHashArchive,
        priceOffersEnabled,
        hrEnabled,
        revisionsEnabled,
        formsEnabled,
        revisionTemplatesEnabled,
        logoFileId,
      },
      parameters: {
        organizationId: organizationId!,
      },
    })
      .then(() => {
        notifications.show({
          title: 'Organizácia bola upravená',
          message: `Organizácia ${organizationName} bola úspešne upravená.`,
          color: SUCCESS_NOTIFICATION_COLOR,
        });

        if (ORGANIZATION_REDIRECT_AFTER_SAVE === 'list') {
          navigate(ORGANIZATIONS_PAGE_PATH.original);
        }
      })
      .catch(panic);
  }

  return (
    <DashboardLayout
      title={`Úprava organizácie: ${data?.organizationName ?? ''}`}
      breadcrumbs={[
        { title: 'Organizácie', link: ORGANIZATIONS_PAGE_PATH.original },
        { title: data?.organizationName ?? 'Načítavanie ...' },
      ]}
    >
      {!loading && (
        <OrganizationForm
          initialValues={{
            organizationName: data.organizationName,
            officialName: data.officialName,
            streetAddress: data.streetAddress,
            city: data.city,
            zip: data.zip,
            country: data.country,
            ico: data.ico ?? '',
            dic: data.dic ?? '',
            icdph: data.icdph ?? '',
            includedInStatistics: data.includedInStatistics,
            inKepHashArchive: data.inKepHashArchive,
            priceOffersEnabled: data.priceOffersEnabled,
            hrEnabled: data.hrEnabled,
            revisionsEnabled: data.revisionsEnabled,
            formsEnabled: data.formsEnabled,
            revisionTemplatesEnabled: data.revisionTemplatesEnabled,
            logoFileId: data.logoFileId ?? '',
            companyQualifications: (data.companyDetail?.qualifications || []).map((qualification) => ({
              ...qualification,
              type: getDeviceType(qualification.deviceTypeId)!.slug,
              uuid: nanoid(),
            })),
            people: (data.people || []).reduce((acc, curr) => {
              acc[String(curr.userId)] = {
                checked: true,
                deleted: curr.deleted,
                permissions: curr.permissions,
                templateName: getPermissionTemplate(curr.templateId)?.slug ?? 'no-template',
                isNew: curr.isNew,
              };

              return acc;
            }, {} as { [userId: string]: { checked: boolean; deleted: boolean; permissions: number[]; templateName: string; isNew: boolean } }),
            peopleInDepartments: (data.peopleInDepartments || []).reduce((acc, curr) => {
              if (!Object.keys(acc).includes(String(curr.departmentId))) {
                acc[String(curr.departmentId)] = [];
              }
              acc[String(curr.departmentId)] = [
                ...acc[String(curr.departmentId)],
                {
                  userId: curr.userId,
                  permissions: curr.permissions,
                  templateName: getPermissionTemplate(curr.templateId)?.slug ?? 'no-template',
                  departmentName: curr.departmentName,
                },
              ];

              return acc;
            }, {} as { [departmentId: string]: { userId: number; permissions: number[]; templateName: string; departmentName: string }[] }),
            hierarchyAssignment: data.hierarchyAssignment,
          }}
          onSubmit={onSubmit}
        />
      )}
    </DashboardLayout>
  );
}
